/**
 * Standardized Layout Utilities
 * 
 * This file contains standardized layout patterns and spacing utilities
 * to ensure consistent spacing and layout across the application.
 */

import { cn } from "@/lib/utils";

// Standard spacing scale
export const spacing = {
  // Vertical spacing (space-y-*)
  tight: 'space-y-2',       // 8px - Within components, related items
  standard: 'space-y-4',    // 16px - Form fields, list items
  section: 'space-y-6',     // 24px - Page sections, card groups
  major: 'space-y-8',       // 32px - Major sections, form cards
  page: 'space-y-12',       // 48px - Page-level sections
  
  // Horizontal spacing (gap-*)
  gapTight: 'gap-2',        // 8px - Icons, small elements
  gapStandard: 'gap-3',     // 12px - Default flex spacing
  gapComfortable: 'gap-4',  // 16px - Grid layouts, buttons
  gapSpacious: 'gap-6',     // 24px - Card grids, dashboard
  gapWide: 'gap-8',         // 32px - Major layout sections
} as const;

// Card padding patterns
export const cardPadding = {
  header: 'p-6 pb-3',       // Header with content below
  headerOnly: 'p-6',        // Header without content
  content: 'p-6 pt-0',      // Content following header
  contentStandalone: 'p-6', // Content without header
  footer: 'p-6 pt-0',       // Footer following content
} as const;

// Table padding patterns
export const tablePadding = {
  cell: 'px-4 py-4',        // Standard table cells
  headerCell: 'px-4 py-3',  // Table headers
  compactCell: 'px-3 py-2', // Dense tables
} as const;

// Container patterns
export const containers = {
  page: 'container mx-auto px-4 py-6',
  section: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  content: 'flex-1 overflow-hidden p-6',
  contentMobile: 'flex-1 overflow-hidden p-4 md:p-6',
} as const;

// Grid patterns
export const grids = {
  dashboard: 'grid gap-4 md:grid-cols-2 lg:grid-cols-4',
  cards: 'grid gap-6 md:grid-cols-2 lg:grid-cols-3',
  formTwoCol: 'grid grid-cols-2 gap-4',
  formThreeCol: 'grid grid-cols-3 gap-4',
  responsive: 'grid gap-4 md:grid-cols-2 lg:grid-cols-3',
  autoFit: 'grid gap-4',
} as const;

// Flex patterns
export const flex = {
  between: 'flex items-center justify-between',
  center: 'flex items-center justify-center',
  start: 'flex items-center',
  end: 'flex items-center justify-end',
  col: 'flex flex-col',
  colCenter: 'flex flex-col items-center',
} as const;

// Button patterns
export const buttons = {
  group: 'flex gap-2',
  formActions: 'flex justify-end gap-2 pt-4',
  mobileFormActions: 'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:pt-4',
} as const;

// Layout utility functions
export const layoutUtils = {
  /**
   * Get standardized card classes
   */
  getCardClasses: (
    hasHeader = false,
    hasFooter = false,
    className?: string
  ) => {
    const baseClasses = 'rounded-lg border bg-card text-card-foreground shadow-sm';
    return cn(baseClasses, className);
  },

  /**
   * Get standardized card header classes
   */
  getCardHeaderClasses: (standalone = false, className?: string) => {
    const baseClasses = standalone ? cardPadding.headerOnly : cardPadding.header;
    return cn(baseClasses, 'flex flex-col space-y-1.5', className);
  },

  /**
   * Get standardized card content classes
   */
  getCardContentClasses: (followsHeader = false, className?: string) => {
    const baseClasses = followsHeader ? cardPadding.content : cardPadding.contentStandalone;
    return cn(baseClasses, className);
  },

  /**
   * Get standardized card footer classes
   */
  getCardFooterClasses: (className?: string) => {
    return cn(cardPadding.footer, 'flex items-center', className);
  },

  /**
   * Get responsive grid classes
   */
  getResponsiveGridClasses: (
    minWidth = '320px',
    gap = 'gap-4',
    className?: string
  ) => {
    return cn(
      gap,
      className
    );
  },

  /**
   * Get form section classes
   */
  getFormSectionClasses: (className?: string) => {
    return cn(spacing.standard, className);
  },

  /**
   * Get page header classes
   */
  getPageHeaderClasses: (className?: string) => {
    return cn(spacing.section, 'mb-6', className);
  },

  /**
   * Get table row classes
   */
  getTableRowClasses: (interactive = true, className?: string) => {
    const baseClasses = 'border-b transition-colors';
    const interactiveClasses = interactive 
      ? 'hover:bg-muted/50 cursor-pointer' 
      : '';
    return cn(baseClasses, interactiveClasses, className);
  },

  /**
   * Get list item classes
   */
  getListItemClasses: (interactive = true, className?: string) => {
    const baseClasses = 'p-4 border rounded-lg';
    const interactiveClasses = interactive 
      ? 'hover:bg-muted/50 cursor-pointer transition-colors' 
      : '';
    return cn(baseClasses, interactiveClasses, className);
  },
};

// Modal/Dialog size presets
export const dialogSizes = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg', 
  lg: 'sm:max-w-xl',
  xl: 'sm:max-w-4xl',
  full: 'sm:max-w-[90vw]',
} as const;

// Form layout presets
export const formLayouts = {
  /**
   * Standard single-column form
   */
  singleColumn: cn(spacing.standard),
  
  /**
   * Two-column responsive form
   */
  twoColumnResponsive: cn('grid gap-4 md:grid-cols-2'),
  
  /**
   * Form with sections (cards)
   */
  sectioned: cn(spacing.major),
  
  /**
   * Inline form (horizontal)
   */
  inline: cn('flex gap-2 items-end'),
};

// Page layout presets
export const pageLayouts = {
  /**
   * Standard page with container
   */
  standard: cn(containers.page),
  
  /**
   * Full-width page content
   */
  fullWidth: cn('p-6'),
  
  /**
   * Dashboard layout
   */
  dashboard: cn('p-6', spacing.section),
  
  /**
   * Two-column layout
   */
  twoColumn: cn('grid gap-6 lg:grid-cols-3'),
  
  /**
   * Sidebar layout
   */
  withSidebar: cn('flex gap-6'),
};

// Responsive utilities
export const responsive = {
  /**
   * Mobile-first padding
   */
  padding: {
    sm: 'p-4 md:p-6',
    md: 'p-6 md:p-8',
    lg: 'p-8 md:p-12',
  },
  
  /**
   * Mobile-first margins
   */
  margin: {
    sm: 'm-4 md:m-6',
    md: 'm-6 md:m-8', 
    lg: 'm-8 md:m-12',
  },
  
  /**
   * Mobile-first gaps
   */
  gap: {
    sm: 'gap-3 md:gap-4',
    md: 'gap-4 md:gap-6',
    lg: 'gap-6 md:gap-8',
  },
  
  /**
   * Mobile-first spacing
   */
  spacing: {
    sm: 'space-y-3 md:space-y-4',
    md: 'space-y-4 md:space-y-6',
    lg: 'space-y-6 md:space-y-8',
  },
};

// Layout validation utilities
export const validateLayout = {
  /**
   * Check if spacing value follows standard scale
   */
  isStandardSpacing: (value: string): boolean => {
    const standardValues = [
      'space-y-2', 'space-y-4', 'space-y-6', 'space-y-8', 'space-y-12',
      'gap-2', 'gap-3', 'gap-4', 'gap-6', 'gap-8',
      'p-2', 'p-3', 'p-4', 'p-6', 'p-8',
    ];
    return standardValues.includes(value);
  },
  
  /**
   * Get suggestions for non-standard spacing
   */
  suggestStandardSpacing: (value: string): string[] => {
    const suggestions: Record<string, string[]> = {
      'space-y-1': ['space-y-2'],
      'space-y-3': ['space-y-2', 'space-y-4'],
      'space-y-5': ['space-y-4', 'space-y-6'],
      'gap-5': ['gap-4', 'gap-6'],
      'p-5': ['p-4', 'p-6'],
    };
    return suggestions[value] || [];
  },
};

export type SpacingKey = keyof typeof spacing;
export type CardPaddingKey = keyof typeof cardPadding;
export type GridKey = keyof typeof grids;
export type FlexKey = keyof typeof flex;